<div class="text-fg flex min-h-full w-full max-w-none flex-col">
  <!-- Header -->
  <header class="mb-5 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
    <div>
      <button type="button" class="cursor-pointer border-0 bg-transparent p-0 text-xs text-muted hover:text-fg" (click)="goBack()">&larr; Back</button>
      @if (wallet; as w) {
        <h1 class="mb-1.5 mt-1 text-[1.4rem] font-extrabold tracking-tight text-fg">Wallet <span class="text-base font-normal text-muted">{{ w['currency_code'] }} · {{ w['country_code'] }}</span></h1>
        <div class="flex flex-wrap gap-1.5">
          <span class="rounded px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide bg-info-bg text-info-fg">{{ w['merchant_id'] }}</span>
          <span class="rounded px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide bg-warning-bg text-warning-fg">{{ w['currency_code'] }} ({{ w['currency_name'] }})</span>
          <span class="rounded px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide bg-success-bg text-success-fg">{{ w['country_code'] }} — {{ w['country_name'] }}</span>
          @if (w['is_active'] === false) { <span class="rounded px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide bg-danger-bg text-danger-fg">Inactive</span> }
        </div>
      } @else {
        <h1 class="mb-1.5 mt-1 text-[1.4rem] font-extrabold tracking-tight text-fg">Wallet Detail</h1>
      }
    </div>
    <div class="flex flex-wrap items-center gap-3">
      <div class="flex flex-wrap items-center gap-2">
        <button type="button" uiButton variant="primary" size="sm" (click)="openFundWizard()" [disabled]="loading() || !wallet">
          <ng-icon name="lucidePlus" size="14" />
          <span i18n="@@wallet.action.fundOperator">Fund via operator</span>
        </button>
        <button type="button" uiButton variant="outline" size="sm" (click)="openTopupModal()" [disabled]="loading() || !wallet">
          <ng-icon name="lucidePlus" size="14" />
          <span i18n="@@wallet.action.bankWire">Bank wire topup</span>
        </button>
        <button type="button" uiButton variant="outline" size="sm" title="Refresh" (click)="loadHub()" [disabled]="loading()">
          <ng-icon name="lucideRefreshCw" size="16" />
        </button>
      </div>
    </div>
  </header>

  @if (actionMessage(); as msg) {
    <div class="mb-3 rounded-lg bg-success-bg px-3.5 py-2.5 text-[0.8rem] font-semibold text-success-fg">{{ msg }}</div>
  }

  @if (loading()) {
    <div class="p-8 text-center text-sm text-muted">Loading wallet data...</div>
  } @else if (error(); as err) {
    <div class="rounded-lg bg-danger-bg p-8 text-center text-sm text-danger-fg">{{ err }}</div>
  } @else if (data(); as hub) {
    <!-- KPI Cards -->
    <section class="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
      <div class="flex items-center gap-3 rounded-[0.625rem] border border-border bg-card p-4">
        <div class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-info-bg text-info-fg"><ng-icon name="lucideBanknote" size="20" /></div>
        <div class="flex min-w-0 flex-col">
          <span class="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">Balance</span>
          <span class="truncate font-mono text-[1.1rem] font-extrabold text-fg">{{ hub.wallet['currency_symbol'] || hub.wallet['currency_code'] }} {{ formatNumber('' + (hub.wallet['balance'] || 0)) }}</span>
        </div>
      </div>
      <div class="flex items-center gap-3 rounded-[0.625rem] border border-border bg-card p-4">
        <div class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-warning-bg text-warning-fg"><ng-icon name="lucideShieldCheck" size="20" /></div>
        <div class="flex min-w-0 flex-col">
          <span class="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">Reserved</span>
          <span class="truncate font-mono text-[1.1rem] font-extrabold text-fg">{{ formatNumber('' + (hub.wallet['reserved_balance'] || 0)) }}</span>
        </div>
      </div>
      <div class="flex items-center gap-3 rounded-[0.625rem] border border-border bg-card p-4">
        <div class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-success-bg text-success-fg"><ng-icon name="lucideTrendingUp" size="20" /></div>
        <div class="flex min-w-0 flex-col">
          <span class="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">Available</span>
          <span class="truncate font-mono text-[1.1rem] font-extrabold text-fg">{{ formatNumber('' + (hub.wallet['available_balance'] || 0)) }}</span>
        </div>
      </div>
      <div class="flex items-center gap-3 rounded-[0.625rem] border border-border bg-card p-4">
        <div class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-row-hover text-brand"><ng-icon name="lucideClipboardList" size="20" /></div>
        <div class="flex min-w-0 flex-col">
          <span class="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">Statements</span>
          <span class="truncate font-mono text-[1.1rem] font-extrabold text-fg">{{ hub.summary.total_statements }}</span>
        </div>
      </div>
      <div class="flex items-center gap-3 rounded-[0.625rem] border border-border bg-card p-4">
        <div class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-row-hover text-brand"><ng-icon name="lucideTrendingDown" size="20" /></div>
        <div class="flex min-w-0 flex-col">
          <span class="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">Difference</span>
          <span class="truncate font-mono text-[1.1rem] font-extrabold text-fg">{{ formatNumber(hub.summary.difference) }}</span>
        </div>
      </div>
    </section>
    <section class="mb-4 rounded-[0.625rem] border border-border bg-card p-5">
      <h3 class="mb-3.5 border-b border-border pb-2 text-xs font-bold uppercase tracking-wide text-muted">Reconciliation</h3>
      <div class="flex flex-wrap gap-3">
        <div class="flex items-center gap-2 rounded-md border border-border bg-surface-muted px-3 py-2">
          <span class="text-xs text-muted">Wallet balance: {{ formatNumber(hub.summary.wallet_balance) }}</span>
          <span class="text-xs text-muted">Ledger balance: {{ formatNumber(hub.summary.ledger_balance) }}</span>
          <span class="text-xs text-muted">Tagged statements: {{ hub.summary.tagged_statements_count }}</span>
          @if (hub.summary.is_tagged_mismatch) {
            <span class="text-[0.78rem] font-bold text-danger-fg">Tagged mismatch: {{ hub.summary.mismatch_reason || 'Mismatch detected' }}</span>
          } @else {
            <span class="text-[0.78rem] font-bold text-success-fg">No mismatch tag</span>
          }
        </div>
      </div>
    </section>

    <!-- Activity by Type -->
    @if (hub.summary.by_type.length > 0) {
      <section class="mb-4 rounded-[0.625rem] border border-border bg-card p-5">
        <h3 class="mb-3.5 border-b border-border pb-2 text-xs font-bold uppercase tracking-wide text-muted">Activity by Type</h3>
        <div class="flex flex-wrap gap-3">
          @for (item of hub.summary.by_type; track item.entry_type) {
            <div class="flex items-center gap-2 rounded-md border border-border bg-surface-muted px-3 py-2">
              <span class="rounded px-2.5 py-1 text-xs font-semibold" [class]="entryTypeClass(item.entry_type)">{{ item.entry_type }}</span>
              <span class="text-xs text-muted">{{ item.count }} entries</span>
              <span class="font-mono text-[0.78rem] font-bold" [class]="isPositive(item.total_amount) ? 'text-success-fg' : 'text-danger-fg'">
                {{ formatNumber(item.total_amount) }}
              </span>
            </div>
          }
        </div>
      </section>
    }

    <!-- Wallet statements (dynamic list: search, sort, filters, pagination) -->
    <section class="mb-0 flex min-h-0 flex-1 flex-col rounded-[0.625rem] border border-border bg-card p-5 [&>lib-dynamic-list]:min-h-0 [&>lib-dynamic-list]:flex-1">
      <h3 class="mb-3.5 border-b border-border pb-2 text-xs font-bold uppercase tracking-wide text-muted">Wallet Statements</h3>
      <lib-dynamic-list
        [listPath]="statementsListPath"
        [extraQueryParams]="statementExtraParams"
        (rowSelect)="onStatementRowSelect($event)"
      />
    </section>
  }

  @if (topupModalOpen()) {
    <ui-modal title="Top up wallet" [open]="true" (closed)="closeTopupModal()">
      <p class="mb-3 text-sm text-muted">
        Funds from bank transfer account for
        <strong>{{ currencyCode() || '—' }} · {{ countryCode() || '—' }}</strong>
      </p>
      <ui-form-field label="Amount" for="wallet-topup-amount">
        <input
          id="wallet-topup-amount"
          type="number"
          step="0.01"
          min="0.01"
          uiInput
          [(ngModel)]="topupAmount"
          [disabled]="saving()"
        />
      </ui-form-field>
      <ng-container modal-footer>
        <button type="button" uiButton variant="outline" (click)="closeTopupModal()" [disabled]="saving()">Cancel</button>
        <button
          type="button"
          uiButton
          variant="primary"
          (click)="submitTopup()"
          [disabled]="saving() || !topupAmount"
        >
          {{ saving() ? 'Processing…' : 'Confirm topup' }}
        </button>
      </ng-container>
    </ui-modal>
  }

  @if (fundWizardOpen() && marketCurrencyCode()) {
    <app-topup-wizard
      mode="wallet-fund"
      [marketCurrencyCode]="marketCurrencyCode()"
      [marketCurrencyId]="marketCurrencyId()"
      [displayCurrencyCode]="currencyCode()"
      [walletId]="id"
      (completed)="onFundCompleted()"
      (cancelled)="closeFundWizard()"
    />
  }
</div>
